import {Component, OnInit, ViewChild} from '@angular/core';
import {BaseLocationModel, BimSelectedElement} from '@cas-hub/acabim-bim-visualiser-client';
import {
    AcabimProject,
    AcabimProjectBuildingStatus,
    AcabimSite,
    BuildingApiService,
    BuildingElementQueryOptions,
    BuildingSpace,
    BuildingSpaceGroup,
    BuildingStorey,
    ColumnFilter,
    DataTableColumnData,
    DatatableComponent,
    DataTableConfig,
    DataTableDataSource,
    DataTableObservableCompletionListener,
    DataTableObservableDataSource,
    DataTableObservableErrorListener,
    DataTableObservableRefresher, isPaginated,
    ProjectApiService,
    SpaceApiService,
    Status,
    TableAction
} from '@cas-hub/acabim-common-client';
import {MessageService, PrimeNGConfig} from 'primeng/api';
import {tap} from 'rxjs/operators';

@Component({
    selector: 'acabim-admin-space-grouping',
    templateUrl: './space-grouping.component.html',
    styleUrls: ['./space-grouping.component.scss'],
    providers: [MessageService]
})
export class SpaceGroupingComponent implements OnInit {

    constructor(
        private readonly projectService: ProjectApiService,
        private readonly spaceApiService: SpaceApiService,
        private readonly buildingApiService: BuildingApiService,
        private messageService: MessageService,
        private primengConfig: PrimeNGConfig
    ) {
    }

    @ViewChild('modalTable') public modalTable?: DatatableComponent;
    @ViewChild('dataTable') public dataTable?: DatatableComponent;

    sites: AcabimSite[] = [];
    selectedSite?: AcabimSite;
    selectedProject?: AcabimProject;
    isLoading = false;
    projects: AcabimProject[] = [];
    spaceGuids: string[] = [];
    spaceDataDisplay: string[][] = [];
    index = 0;
    showViewer = false;
    spaceDataLoading = false;
    newGroupName = '';
    newGroupDescription = '';
    spaceGroupsInBuilding: BuildingSpaceGroup[] = [];
    selectedGroup?: BuildingSpaceGroup;
    stories: BuildingStorey[] = [];
    spaces: BuildingSpace[] = [];
    selectedStorey?: BuildingStorey;
    selectedSpace?: BuildingSpace = undefined;
    public dataSources: DataTableDataSource<any>[] = [];
    public modalDS: DataTableDataSource<any>[] = [];

    viewerSelectedSpace?: string;
    showAction = false;
    actionId?: number;
    floorCardLoading = false;

    ngOnInit(): void {
        this.primengConfig.ripple = true;
        this.modalDS.push(new SpaceGroupDS(this.spaceModalDataRefresher, undefined, undefined, undefined, false, false));
        this.dataSources.push(new SpaceGroupDS(this.spaceDataRefresher, undefined, undefined, action => {
            if (action.id === 0) {
                if (this.dataSources[0].selectedData.length > 0) {
                    this.dataSources[0].selectedData.forEach(space => {
                        this.removeSpaceId(space.guid);
                    });
                    this.dataSources[0].selectedData = [];
                    this.dataTable?.requestImmediateDataRefresh();
                } else {
                    this.messageService.clear();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Please select at least one space'
                    });
                }
            } else {
                if (this.dataSources[0].selectedData.length > 0) {
                    this.actionId = action.id;
                    this.showAction = true;
                    this.modalTable?.requestImmediateDataRefresh();
                } else {
                    this.messageService.clear();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Please select at least one space'
                    });
                }
            }
        }));
        this.isLoading = true;
    }

    removeSpaceId(id: string) {
        this.spaceGuids = this.spaceGuids.filter(item => item !== id);
    }

    setLocation(value: BaseLocationModel): void {
        console.dir(value);
        if (value && value.site) {
            this.selectedSite = value.site;
        }
        if (value && value.project) {
            this.selectedProject = value.project;
            this.refreshGroupList();
        }
        if (value && value.storeys) {
            this.stories = value.storeys;
        }
        if (value && value.storey) {
            this.selectedStorey = value.storey;
        }
        if (value && value.spaces) {
            this.spaces = value.spaces;
        }
        if (value && value.space) {
            this.selectedSpace = value.space;
        }
    }

    getBuildings(siteId: string) {
        this.spaceGuids = [];
        this.dataTable?.requestImmediateDataRefresh();
        this.selectedProject = undefined;
        this.projects = [];
        const options = {filterStatus: Status.active, filterBuildingStatus: AcabimProjectBuildingStatus.BuildingAvailable};
        this.projectService.getActiveProjectsInSite(siteId, undefined, options, false)
            // The response will never be paginated - but we need a type guard here for the ts compiler
            .subscribe((val: any) => {
                this.projects = val as AcabimProject[];
            });
    }

    getStories(buildingGUID: string) {
        this.stories = [];
        this.spaces = [];
        this.selectedStorey = undefined;
        this.selectedSpace = undefined;
        this.buildingApiService.getBuildingStories(buildingGUID).subscribe(storey => {
            this.stories = storey;
        });
    }

    getGroups(buildingGUID: string) {
        this.spaceGuids = [];
        this.dataTable?.requestImmediateDataRefresh();
        this.refreshGroupList();
        this.getStories(buildingGUID);
    }

    getSelectedSpaces(selected: BimSelectedElement): void {
        if (selected.selectedElements && selected.selectedElements.length > 0) {
            this.viewerSelectedSpace = selected.selectedElements[0].guid;
        }
    }

    addSpaceFromDropdown(guid?: string) {
        if (this.showViewer) {
            this.showViewer = false;
        }
        if (guid) {
            this.isLoading = true;
            if (this.spaceGuids.includes(guid)) {
                this.isLoading = false;
                this.messageService.clear();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Space already in list'
                });
            } else {
                this.spaceGuids.push(guid);
                this.dataTable?.requestImmediateDataRefresh();
                this.isLoading = false;
                this.messageService.clear();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Space Added'
                });
            }
        } else {
            this.messageService.clear();
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select space from dropdown'
            });
        }
    }

    private get spaceDataRefresher(): DataTableObservableRefresher<BuildingSpace> {
        if (this.selectedProject) {
            const options: Partial<BuildingElementQueryOptions> = {
                buildingGuid: [this.selectedProject.buildingGuid],
                spaceGuid: this.spaceGuids
            };

            // @ts-ignore
            return () => this.buildingApiService.getBuildingSpaceInfo(this.selectedProject.id, options, true).pipe(tap(data => {
                    data.forEach(d => {
                        let groups = '';
                        d.groups.forEach(g => {
                            groups = groups + g.name + ',';
                        });
                        if (groups.length > 0) {
                            groups = groups.slice(0, -1);
                        }
                        d.groupNames = groups;
                    });
                }
            ));
        }

        return () => null;
    }

    private get spaceModalDataRefresher(): DataTableObservableRefresher<BuildingSpace> {
        if (this.selectedProject) {
            const options: Partial<BuildingElementQueryOptions> = {
                buildingGuid: [this.selectedProject.buildingGuid],
                spaceGuid: this.dataSources[0].selectedData.map(space => space.guid)
            };

            // @ts-ignore
            return () => this.buildingApiService.getBuildingSpaceInfo(this.selectedProject.id, options, true).pipe(tap(data => {
                    data.forEach(d => {
                        let groups = '';
                        d.groups.forEach(g => {
                            groups = groups + g.name + ',';
                        });
                        if (groups.length > 0) {
                            groups = groups.slice(0, -1);
                        }
                        d.groupNames = groups;
                    });
                }
            ));
        }

        return () => null;
    }


    addToNewGroup() {
        if (this.newGroupName && this.newGroupDescription && this.selectedProject) {
            const spaceGUID: string[] = [];
            this.dataSources[0].selectedData.forEach(space => {
                spaceGUID.push(space.guid);
            });
            this.spaceDataLoading = true;

            this.spaceApiService.createSpaceGroup(this.selectedProject.id, this.newGroupName, this.newGroupDescription, spaceGUID).subscribe((data: any) => {
                this.modalTable?.requestImmediateDataRefresh();
                this.messageService.add({
                    key: 'modal',
                    severity: 'success',
                    summary: 'Success',
                    detail: this.newGroupName + ' created, Space(s) have been added to selected group'
                });
                this.newGroupDescription = '';
                this.newGroupName = '';
                this.spaceDataLoading = false;
            });
        } else {
            this.messageService.clear();
            this.messageService.add({
                key: 'modal',
                severity: 'error',
                summary: 'Error',
                detail: 'Please add the name and description for the group'
            });
        }
    }

    selectTab($event: { index: number; }) {
        this.index = $event.index;
        this.refreshGroupList();
    }


    addToExistingGroup() {
        if (this.selectedGroup) {
            const spaceGUID: string[] = [];
            this.dataSources[0].selectedData.forEach(space => {
                spaceGUID.push(space.guid);
            });
            this.spaceDataLoading = true;

            this.spaceApiService.attachSpacesToGroup(this.selectedGroup.id, spaceGUID).subscribe((result: any) => {
                this.messageService.clear();
                this.messageService.add({
                    key: 'modal',
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Space(s) added to selected group'
                });
                this.modalTable?.requestImmediateDataRefresh();
                this.selectedGroup = undefined;
                this.spaceDataLoading = false;
            }, () => {
                this.spaceDataLoading = false;
                this.messageService.clear();
                this.messageService.add({
                    key: 'modal',
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Sorry, cannot add to or remove that space from the group.'
                });
            });
        } else {
            this.messageService.clear();
            this.messageService.add({
                key: 'modal',
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a group to add'
            });
        }
    }

    getSpaces(guid: string) {
        this.spaces = [];
        this.selectedSpace = undefined;
        this.floorCardLoading = true;
        this.buildingApiService.getBuildingSpaceInfo(this.selectedProject?.id as string, {buildingStoreyGuid: [guid]}).subscribe(spaces => {
            this.floorCardLoading = false;
            this.spaces = spaces;
        });
    }

    removeFromExistingGroup() {
        if (this.selectedGroup) {
            const spaceGUID: string[] = [];
            this.dataSources[0].selectedData.forEach(space => {
                spaceGUID.push(space.guid);
            });
            this.spaceDataLoading = true;

            this.spaceApiService.detachSpaceFromGroup(this.selectedGroup.id, spaceGUID).subscribe((result: any) => {
                this.messageService.clear();
                this.messageService.add({
                    key: 'modal',
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Space(s) removed from selected group'
                });
                this.modalTable?.requestImmediateDataRefresh();
                this.selectedGroup = undefined;
                this.spaceDataLoading = false;
            }, (error) => {
                this.messageService.clear();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.error ? error.errpor : 'Sorry, cannot add to or remove that space from the group.'
                });
            });
        } else {
            this.messageService.clear();
            this.messageService.add({
                key: 'modal',
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a group to remove'
            });
        }
    }

    showViewerAction(): any {
        this.showViewer = true;
    }

    setViewerStatus($event: boolean) {
        this.showViewer = $event;
    }

    deleteGroup(): void {
        if (this.selectedGroup) {
            this.spaceDataLoading = true;
            this.spaceApiService.deleteSpaceGroup(this.selectedGroup.id).subscribe(data => {
                this.messageService.clear();
                this.messageService.add({
                    key: 'modal',
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Group Deleted'
                });
                this.refreshGroupList();
            }, error => {
                this.spaceDataLoading = false;
                this.messageService.clear();
                this.messageService.add({
                    key: 'modal',
                    severity: 'error',
                    summary: 'Error',
                    detail: error.error
                });
            });
        } else {
            this.messageService.clear();
            this.messageService.add({
                key: 'modal',
                severity: 'error',
                summary: 'Error',
                detail: 'Please select group to delete'
            });
        }
    }

    refreshGroupList() {
        if (this.selectedProject) {
            this.spaceGroupsInBuilding = [];
            this.spaceApiService.getAllSpaceGroups({filterProjectId: this.selectedProject.id}).subscribe(v => {
                this.spaceGroupsInBuilding = isPaginated(v) ? v.data : v;
            });
        }
    }

    modalStatus($event: boolean) {
        this.showAction = $event;
        if (!$event) {
            this.dataSources[0].selectedData = [];
            this.dataTable?.requestImmediateDataRefresh();
        }
    }
}


export class SpaceGroupDS<T extends BuildingSpace> extends DataTableObservableDataSource<T> {

    readonly columnData: DataTableColumnData[] =
        [
            {displayName: 'Name', objectPropertyName: 'name'},
            {displayName: 'Long Name', objectPropertyName: 'longName'},
            {displayName: 'Group Names', objectPropertyName: 'groupNames'},
            {displayName: 'Remarks', objectPropertyName: 'remarks'}
        ];
    config: DataTableConfig;
    columnFilters: ColumnFilter[] = [];
    actions: TableAction[] = [{name: 'Remove From Selection', id: 0}, {name: 'Add To Group', id: 1}, {name: 'Remove From Group', id: 2}];

    onDataExpanded(value: BuildingSpace): void {
    }

    constructor(readonly dataRefresher: DataTableObservableRefresher<T>,
                readonly observableErrorListener?: DataTableObservableErrorListener,
                readonly observableCompleteListener?: DataTableObservableCompletionListener,
                readonly actionListener?: (action: TableAction) => void,
                readonly hasCheckbox: boolean = true,
                readonly hasActions: boolean = true
    ) {
        super(() => this.dataRefresher(),
            undefined, undefined);

        this.config = {
            tableHeading: '',
            isSearchEnabled: false,
            searchPlaceHolder: '',
            emptyMessage: 'No space selected',
            globalFilterFields: [],
            hasCheckbox: this.hasCheckbox,
            hasDetailsButton: false,
            isLazyLoadingEnabled: false,
            isPaginationEnabled: false,
            hasFilters: false,
            isExportEnabled: false,
            hasActions: this.hasActions
        };
    }

    onAction(action: TableAction): void {
        if (this.actionListener) {
            this.actionListener(action);
        }
    }

    onDataFiltered(): void {
    }
}
